/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  IconButton,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { addNewCompany } from "../../../../Services/redux/global-admin-users/GlobalAdminUsersActions";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const AddCompanyModal = ({companies, openAddCompanyModal, handleAddCompanyClose }) => {
  const dispatch = useDispatch();
  const [companyName, setCompanyName] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [touched, setTouched] = useState({
    companyName: false,
    companyWebsite: false,
    privacyPolicy: false,
  });

  const isEmpty = (val) => !val.trim();
  // General URL validation allowing common TLDs and any 2-letter ccTLD; supports multi-level domains (e.g. company.co.in)
  const isValidUrl = (val) => {
    const raw = val.trim();
    if (!raw) return false;

    // Prepend protocol if missing to allow parsing "www.example.com" or "example.com"
    const withProtocol = /^(https?:\/\/)/i.test(raw) ? raw : `https://${raw}`;
    let urlObj;
    try {
      urlObj = new URL(withProtocol);
    } catch {
      return false;
    }

    const host = urlObj.hostname;
    // Must contain at least one dot (simple hostname like "localhost" rejected)
    if (!host.includes(".")) return false;

    const labels = host.split(".").filter(Boolean);
    const lastLabel = labels.at(-1);

    // Allowed generic TLD set + any 2-letter ccTLD
    const allowedGenerics = new Set([
      "com",
      "org",
      "net",
      "io",
      "info",
      "biz",
      "dev",
      "app",
      "ai",
      "tech",
      "gov",
      "edu",
      "co",
    ]);
    const isCcTld = /^[a-z]{2}$/i.test(lastLabel);
    const isGeneric = allowedGenerics.has(lastLabel.toLowerCase());
    if (!isCcTld && !isGeneric) return false; // reject unknown long TLDs like "commmm"

    // Basic hostname label format checks (each label 1-63 chars, no leading/trailing hyphen)
    if (labels.some((l) => l.length < 1 || l.length > 63 || /(?:^-|-$)/.test(l)))
      return false;
    if (/[^a-zA-Z0-9.-]/.test(host)) return false; // invalid chars

    return true;
  };

  const addCompanyName = () => {
    setTouched({
      companyName: true,
      companyWebsite: true,
      privacyPolicy: true,
    });
    if (
      isEmpty(companyName) ||
      isEmpty(companyWebsite)
    ) {
      return;
    }

    dispatch(
      addNewCompany({
        name: companyName.trim(),
        website: companyWebsite.trim(),
        privacyPolicy: privacyPolicy.trim(),
      })
    );
    handleAddCompanyClose();
    setCompanyName("");
    setCompanyWebsite("");
    setPrivacyPolicy("");
    setTouched({
      companyName: false,
      companyWebsite: false,
      privacyPolicy: false,
    });
  };

  const isDuplicateCompanyName = () => {
    if (!companyName.trim()) return false;
    const companyNamesArray = companies.map((value) => value?.name?.toLowerCase().trim());
    return companyNamesArray.includes(companyName.toLowerCase().trim());
  };
  const isDuplicateCompanyWebsite = () => {
    if (!companyWebsite.trim()) return false;
    const companyWebsiteArray = companies.map((value) => value?.website?.toLowerCase().trim());
    return companyWebsiteArray.includes(companyWebsite.toLowerCase().trim());
  };

  const getCompanyNameError = () => {
    if (touched.companyName && isEmpty(companyName)) {
      return "Company Name is required";
    }
    if (isDuplicateCompanyName()) {
      return "* This company name is already present, please give new company name";
    }
    return "";
  };

  const getCompanyWebsiteError = () => {
    if (touched.companyWebsite && isEmpty(companyWebsite)) {
      return "Company Website is required";
    }
    if (touched.companyWebsite && !isEmpty(companyWebsite) && !isValidUrl(companyWebsite)) {
      return "Invalid pattern.";
    }
    if (isDuplicateCompanyWebsite()) {
      return "* This company website is already present, please give new company website";
    }
    return "";
  };

  const getPrivacyPolicyError = () => {
    return "";
  };
  useEffect(() => {
    if (handleAddCompanyClose) {
      setCompanyName("");
      setCompanyWebsite("");
      setPrivacyPolicy("");
      setTouched({
        companyName: false,
        companyWebsite: false,
        privacyPolicy: false,
      });
    }
  }, [handleAddCompanyClose]);
  return (
    <BootstrapDialog
        onClose={handleAddCompanyClose}
        aria-labelledby="customized-dialog-title"
        open={openAddCompanyModal}
        slotProps={{
          paper: {
            sx: {
              width: "504px",
              height: "auto",
              minHeight: "35px",
              border: "1px solid #1214151A",
              borderRadius: "8px",
              padding: "24px",
              display: "flex",
              gap: "24px",
            },
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "511px",
            height: "auto",
            maxHeight: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row",
            gap: "16px",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              width: "252px",
              height: "26px",
              fontWeight: 500,
              fontSize: "22px",
              lineHeight: "120%",
              letterSpacing: "0%",
              color: "#2A2C2D",
            }}
          >
            Add New Company Name
          </Typography>

          <IconButton
            aria-label="close"
            onClick={handleAddCompanyClose}
            sx={(theme) => ({
              height: "32px",
              width: "32px",
              borderRadius: "4px",
              color: "#12141599",
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: "511px",
            height: "auto",
            minHeight: "289px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Box
            sx={{
              width: "511px",
              height: "auto",
              minHeight: "63px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography
              sx={{
                width: "100%",
                maxWidth: "115px",
                height: "auto",
                maxHeight: "19px",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                color: "#2A2C2D",
                textWrap: "nowrap",
                verticalAlign: "middle",
              }}
            >
              Company Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              placeholder="Enter your company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, companyName: true }))
              }
              fullWidth
              required
              error={
                (touched.companyName && isEmpty(companyName)) ||
                isDuplicateCompanyName()
              }
              helperText={getCompanyNameError()}
              size="small"
              slotProps={{
                htmlInput: { maxLength: 100 },
              }}
              sx={{
                borderRadius: "4px",
                color: "#717273",
              }}
            />
          </Box>

          <Box
            sx={{
              width: "511px",
              height: "auto",
              minHeight: "63px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography
              sx={{
                width: "100%",
                maxWidth: "115px",
                height: "auto",
                maxHeight: "19px",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                color: "#2A2C2D",
                textWrap: "nowrap",
                verticalAlign: "middle",
              }}
            >
              Company Website <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              placeholder="www.website.com"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, companyWebsite: true }))
              }
              fullWidth
              required
              error={
                (touched.companyWebsite &&
                  (isEmpty(companyWebsite) || !isValidUrl(companyWebsite))) ||
                isDuplicateCompanyWebsite()
              }
              helperText={getCompanyWebsiteError()}
              size="small"
              slotProps={{
                htmlInput: { maxLength: 100 },
              }}
              sx={{
                borderRadius: "4px",
                color: "#717273",
              }}
            />
          </Box>

          <Box
            sx={{
              width: "511px",
              height: "auto",
              minHeight: "63px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography
              sx={{
                width: "100%",
                maxWidth: "115px",
                height: "auto",
                maxHeight: "19px",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                color: "#2A2C2D",
                textWrap: "nowrap",
                verticalAlign: "middle",
              }}
            >
              Privacy Policy
            </Typography>
            <TextField
              placeholder="Enter your policy terms link here"
              value={privacyPolicy}
              onChange={(e) => setPrivacyPolicy(e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, privacyPolicy: true }))
              }
              fullWidth
              required
              error={false}
              helperText={getPrivacyPolicyError()}
              size="small"
              slotProps={{
                htmlInput: { maxLength: 100 },
              }}
              sx={{
                borderRadius: "4px",
                color: "#717273",
              }}
            />
          </Box>

          <DialogActions
            sx={{
              width: "511px",
              height: "auto",
              minHeight: "52px",
            }}
          >
            <Button
              sx={{
                textTransform: "none",
              }}
              variant="outlined"
              onClick={handleAddCompanyClose}
            >
              Cancel
            </Button>
            <Button
              sx={{
                textTransform: "none",
              }}
              variant="contained"
              onClick={addCompanyName}
              disabled={
                isEmpty(companyName) ||
                isEmpty(companyWebsite) ||
                !isValidUrl(companyWebsite) ||
                isDuplicateCompanyName() ||
                isDuplicateCompanyWebsite()
              }
            >
              Add
            </Button>
          </DialogActions>
        </Box>
      </BootstrapDialog>
  );
};

AddCompanyModal.propTypes = {
  companies: PropTypes.array.isRequired,
  openAddCompanyModal: PropTypes.bool.isRequired,
  handleAddCompanyClose: PropTypes.func.isRequired,
};

export default AddCompanyModal;
